import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { FloorPlanResponse } from "./floorApi";

/**
 * The FLOOR_PLAN-off view (EN-2): the entitlement is disabled, so every mutation affordance is absent
 * and the DRAWN DATA IS UNTOUCHED — re-enabling the flag restores the exact plan. A read-only summary
 * plus an upgrade prompt; the server also re-enforces the entitlement fail-closed on any write.
 */
export function ReadOnlyPlanView({ plan }: { plan: FloorPlanResponse | null }) {
  const { t } = useTranslation();
  const zones = plan?.zones ?? [];
  const tables = (plan?.tables ?? []).filter((tb) => !tb.retired);

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "4" })}>
      <div
        role="note"
        className={css({
          p: "4",
          borderWidth: "1px",
          rounded: "lg",
          bg: "bg.subtle",
          display: "flex",
          flexDirection: "column",
          gap: "2",
        })}
      >
        <h2 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
          {t("floorPlan.locked.title")}
        </h2>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("floorPlan.locked.subtitle")}
        </p>
      </div>

      {plan && plan.mode === "FLOOR_PLAN" ? (
        <section className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
          <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
            {t("floorPlan.locked.current")}
          </h3>
          <ul className={css({ fontSize: "sm", color: "fg.muted", pl: "4" })}>
            {zones.map((z) => (
              <li key={z.id}>
                {z.name} —{" "}
                {t("floorPlan.locked.tableCount", {
                  count: tables.filter((tb) => tb.zoneId === z.id).length,
                })}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("floorPlan.locked.capacityOnly")}
        </p>
      )}
    </div>
  );
}
